<script setup lang="ts">
import { EventFieldsFragment, ParagraphAnchors, TaxonomyTermInstitutionSector } from '~/api/graphql/generated'
import { Link } from '~/types'
import { ItemProps } from '../sidebar/components/LabelValueSidebar.vue'
import { format, parse, isSameDay } from 'date-fns'
import { de } from 'date-fns/locale'
import { useSchemaEvent } from '~/composables/useSchemaEvent'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  entity: EventFieldsFragment
  breadcrumbs?: Link[]
}>()

const { t } = useI18n()

const startDate = computed(() =>
  props.entity.fieldStartDate?.date
    ? parse(props.entity.fieldStartDate.date, "yyyy-MM-dd HH:mm:ss 'UTC'", new Date())
    : new Date(),
)

const endDate = computed(() => {
  const endDate = props.entity.fieldEndDate?.date
    ? parse(props.entity.fieldEndDate.date, "yyyy-MM-dd HH:mm:ss 'UTC'", new Date())
    : undefined

  return endDate && !isSameDay(startDate.value, endDate) ? endDate : undefined
})

const eventDetails = computed(() => {
  const eventDate = [
    format(startDate.value, 'dd. LLLL yyyy', { locale: de }),
    ...[endDate.value ? format(endDate.value, 'dd. LLLL yyyy', { locale: de }) : false],
  ].filter(Boolean)

  return [
    {
      label: t('event.detail.date'),
      value: `${eventDate.join(' - ')}`,
    },
    ...[
      props.entity.fieldTimeWeekday ? { label: t('event.detail.time'), value: props.entity.fieldTimeWeekday } : false,
    ],
  ].filter(Boolean)
})

const sidebarLogo = computed(() => {
  const fieldInstitution = props.entity.fieldInstitution?.entity as TaxonomyTermInstitutionSector

  return fieldInstitution?.fieldLogo?.url ?? ''
})

const sidebarLocation = computed(() => {
  if (!props.entity.fieldPhysical) return undefined

  const fieldInstitution = props.entity.fieldInstitution?.entity as TaxonomyTermInstitutionSector

  const googleMapsQuery = props.entity.fieldAddress
    ? [
        props.entity.fieldAddress.addressLine1,
        props.entity.fieldAddress.postalCode,
        props.entity.fieldAddress.locality,
        'Switzerland',
      ].join('+')
    : ''

  const label = props.entity.fieldAddress
    ? [
        props.entity.fieldAddress.organization,
        props.entity.fieldAddress.addressLine1,
        props.entity.fieldAddress.locality
          ? `${props.entity.fieldAddress.postalCode} ${props.entity.fieldAddress.locality}`
          : props.entity.fieldAddress.postalCode,
      ]
        .filter(Boolean)
        .join('<br/>')
    : ''

  return {
    title: fieldInstitution?.name ?? '',
    googleMapsQuery,
    label,
  }
})

const additionalInfo = computed(() => {
  return [
    ...[
      props.entity.fieldParticipationPrice
        ? {
            label: t('event.detail.price'),
            value: props.entity.fieldParticipationPrice,
          }
        : false,
    ],
    ...[
      props.entity.fieldPhone
        ? {
            label: t('event.detail.phone'),
            value: `<a class="text-xsmall" href='tel:${props.entity.fieldPhone}'>${props.entity.fieldPhone}</a>`,
          }
        : false,
    ],
  ].filter(Boolean)
})

const sidebarCta = ref({
  to: props.entity.fieldRegistrationCta?.url?.path ?? '',
  title: props.entity.fieldRegistrationCta?.title ?? '',
  target: props.entity.fieldRegistrationCta?.options.attributes?.target ?? '_self',
})

// Add schema.org JSON-LD structured data
useSchemaEvent(props.entity)
</script>

<template>
  <SectionHero :entity="entity" :breadcrumbs="breadcrumbs" />
  <SectionAnchors v-if="entity.fieldAnchors?.entity" :entity="entity.fieldAnchors.entity as ParagraphAnchors" />
  <div class="event-body">
    <SectionSidebar in-flow>
      <LabelValueSidebar :title="t('event.detail.details')" :items="eventDetails as ItemProps[]" />

      <LogoSidebar v-if="sidebarLogo" :image="sidebarLogo" />

      <LocationSidebar v-if="sidebarLocation && !sidebarLogo" v-bind="sidebarLocation" />

      <LabelValueSidebar :items="additionalInfo as ItemProps[]" />

      <TextLinkSidebar v-if="entity.fieldEmail" :to="`mailto:${entity.fieldEmail}`">
        {{ t('event.detail.messageCTA') }}
      </TextLinkSidebar>

      <CtaSidebar v-if="entity.fieldRegistrationCta" v-bind="sidebarCta" />
    </SectionSidebar>
    <div class="event-sections">
      <Sections :entity="entity" />
    </div>
  </div>
  <SectionEventRelated
    :category-id="entity.category?.entity?.tid ?? undefined"
    :type-id="entity.fieldEventType?.entity?.tid ?? undefined"
    :current-event-id="entity.nid ?? undefined"
  />
</template>

<style scoped lang="scss">
.event-body {
  @include fluid(--margin, 96px, 128px);
  margin-top: var(--margin);
  margin-bottom: var(--margin);

  // Replaces SectionSidebar's `.section-sidebar + .section { margin-top: 0 }`
  // sibling rule, which no longer matches across the wrapper (all widths, like the
  // old rule).
  .event-sections > :deep(.section:first-child) {
    margin-top: 0;
  }

  @include breakpoint(tl) {
    display: grid;

    > .section-sidebar,
    > .event-sections {
      grid-area: 1 / 1;
    }

    > .section-sidebar {
      margin-top: 0; // grid-item margins don't collapse; both layers start at cell top
      z-index: 1; // paint above the text layer, like the old absolute overlay
    }

    .event-sections > :deep(.section:last-child) {
      margin-bottom: 0; // the wrapper's own margin-bottom provides the spacing
    }
  }
}
</style>
